<template>
  <EditorLayout
    :isLoading="isLoading"
    :error="error"
    :visibleRepeatBtn="false"
  >
    <template v-if="newsItem">
      <div class="editor-by-id-workspace__header workspace-header">
        <EditorActionButtons
          :statusId="newsItem.version.status_id"
          :isRevoked="newsItem.is_revoked"
          :versionUserId="newsItem.version.user?.id"
          @send="showShareModal({ itemId: newsItem.id, actionType: 'sendToEdit', hasKeepOriginal: true })"
          @sendCopy="showShareModal({ itemId: newsItem.id, actionType: 'sendToEdit', hasKeepOriginal: false })"
          @sendToAsoi="showShareModal({ itemId: newsItem.id, actionType: 'sendToAsoi', hasKeepOriginal: false })"
          @pick="pick(0, newsItem.id)"
          @extend="extend(0, newsItem.id)"
          @republish="republish(0, newsItem.id)"
          @revoke="revoke(0, newsItem.id)"
          @delete="onDeleteHandler(newsItem.id)"
          @dispose="onDisposeHandler(newsItem.id)"
        />

        <div class="workspace-header__secondary-buttons">
          <ActionButton @click="openLegacyEditor(newsItem.id)">
            Открыть в старом редакторе
            <template #icon>
              <Icon
                icon="open-out"
                size="small"
              />
            </template>
          </ActionButton>

          <ActionButton
            :circle="true"
            modification="panel"
            @click="resetCurrentNews"
          >
            <template #icon>
              <Icon
                icon="close"
                size="small"
              />
            </template>
          </ActionButton>
        </div>
      </div>

      <div
        class="editor-by-id-workspace__main editor"
      >
        <div class="editor__wrapper">
          <div class="editor__header">
            <h1
              class="editor__headline"
              v-html="newsItem.version.headline"
            />
            <time
              class="editor__time"
              v-html="new Date(newsItem.version.created_at).toLocaleString(
                undefined,{ dateStyle: 'long', timeStyle: 'medium', })
              "
            />
          </div>

          <h2 v-html="newsItem.id" />
          <h3 v-html="newsItem.version.user?.name" />
          <h3>
            Статус:
            {{ ENewsStatus[newsItem.version.status_id] }} - {{ ENewsStatusLabel[newsItem.version.status_id] }}
            ({{ newsItem.version.status_id }})
          </h3>
          <pre
            class="editor__body"
            v-text="newsItem"
          />
        </div>

        <EditorSecondaryActionButtons
          class="editor__secondary-buttons"
          @showHistory="toggleShowHistory(true)"
          @showRelations="toggleShowRelations(true)"
        />
      </div>

      <NewsHistoryModal
        v-if="showHistory"
        :id="newsItem.id"
        v-model:show="showHistory"
      />

      <NewsRelationsModal
        v-if="showRelations"
        v-model="showRelations"
        :itemId="newsItem.id"
        @open="open"
      />
    </template>

    <ElEmpty
      v-else
      description="Выберите новость"
    />
  </EditorLayout>
</template>

<script setup lang="ts">
import { ElEmpty, ElMessageBox, ElNotification } from 'element-plus'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useRouteParams } from '@vueuse/router'
import { computed, inject, watch } from 'vue'
import { useAsyncState, useToggle } from '@vueuse/core'
import { noop } from 'lodash-es'
import EditorActionButtons from '@/components/domain/EditorActionButtons.vue'
import ActionButton from '@/components/basic/ActionButton/ActionButton.vue'
import { ENewsStatus, ENewsStatusLabel } from '../lib/references.ts'
import { useNewsEditorStore } from '@/stores/newsEditor.ts'
import Icon from '@/components/basic/Icon/Icon.vue'
import EditorLayout from '@/components/domain/EditorLayout.vue'
import { ENABLE_NEW_EDITOR } from '@/config'
import EditorSecondaryActionButtons from '@/components/domain/EditorSecondaryActionButtons.vue'
import NewsHistoryModal from '@/components/domain/NewsHistoryModal.vue'
import NewsRelationsModal from '@/components/domain/NewsRelationsModal.vue'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import { useShareModal } from '@/ContextTab/modules/news/composables/useShareModal'


const { onError } = inject(NotificationHandlerKey, { onError: noop })

const newsEditorStore = useNewsEditorStore()
const router = useRouter()

const newsId = useRouteParams<number | undefined>('id', undefined, {
  transform: Number,
})

if (!ENABLE_NEW_EDITOR) {
  // Временная мера для открытия старого редактора по ID новости
  router.push({ path: `/legacy/news/${newsId.value}` })
}

const {
  resetCurrentNews,
  loadNewsById,
  subscribeGateway,
  unsubscribeGateway,
} = newsEditorStore

const { newsItem, isLoading: isLoadingNews, error } = storeToRefs(newsEditorStore)

const { showShareModal } = useShareModal()

const {
  open,
  pick: pickPromise,
  extend: extendPromise,
  republish: republishPromise,
  revoke: revokePromise,
  deleteNews: deletePromise,
  dispose: disposePromise,
  openByNewsItem,
} = newsEditorStore

const { isLoading: isLoadingPick, execute: pick } = useAsyncState(
  pickPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)

const { isLoading: isLoadingExtend, execute: extend } = useAsyncState(
  extendPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)

const { isLoading: isLoadingRepublish, execute: republish } = useAsyncState(
  republishPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)

const { isLoading: isLoadingRevoke, execute: revoke } = useAsyncState(
  revokePromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)

const { isLoading: isLoadingDelete, execute: deleteNews } = useAsyncState(
  deletePromise,
  undefined,
  { onError, onSuccess: () => {
    ElNotification.success('Новость успешно удалена')
    resetCurrentNews()
  }, immediate: false },
)

const { isLoading: isLoadingDispose, execute: dispose } = useAsyncState(
  disposePromise,
  undefined,
  { onError, onSuccess: () => {
    ElNotification.success('Новость успешно отправлена в отвал')
    resetCurrentNews()
  }, immediate: false },
)

const isLoading = computed(() =>
  isLoadingNews.value
  || isLoadingPick.value
  || isLoadingExtend.value
  || isLoadingRepublish.value
  || isLoadingRevoke.value
  || isLoadingDelete.value
  || isLoadingDispose.value,
)

function onDeleteHandler(newsId: number) {
  ElMessageBox.confirm(
    'Новость будет удалена без возможности восстановления или отправлена в отвал, '
    + 'если есть связанные заметки, отправленнные в АСОИ',
    'Удаление новости / Отправка в отвал',
    {
      confirmButtonText: 'Удалить / В отвал',
      cancelButtonText: 'Отменить действие',
      type: 'warning',
    },
  ).then(() => {
    deleteNews(0, newsId)
  })
}

function onDisposeHandler(newsId: number) {
  ElMessageBox.prompt(
    'Пожалуйста, укажите причину отклонения материала:',
    'Отклонение материала',
    {
      confirmButtonText: 'Отклонить новость',
      cancelButtonText: 'Отмена',
      inputPattern: /.{0,10000}/, // Максимум 10000 символов
      inputErrorMessage: 'Причина должна быть не более 10000 символов',
    },
  ).then(({ value: reason }) => {
    dispose(0, newsId, reason)
  })
}

function openLegacyEditor(id: number) {
  const routeData = router.resolve({ path: `/legacy/news/${id}` })
  window.open(routeData.href, '_blank')
}

watch(newsId, (newValue, oldValue) => {
  if (newValue) {
    loadNewsById(0, newValue)
    subscribeGateway(newValue)
  }

  if (oldValue) {
    unsubscribeGateway(oldValue)
  }
}, {
  immediate: true,
})

const [showHistory, toggleShowHistory] = useToggle()
const [showRelations, toggleShowRelations] = useToggle()
</script>

<style scoped>
.editor-by-id-workspace__header {
  display: flex;
  width: 100%;
  gap: var(--ps-padding-s);
  border-bottom: 1px solid var(--ps-info-text-color);
  padding: var(--ps-padding-m);
}

.workspace-header__secondary-buttons {
  margin-left: auto;
}

.editor-by-id-workspace__main {
  flex: 1;
  width: 100%;
  overflow: auto;
  padding: var(--ps-padding-l);
}

.editor {
  display: flex;
  gap: var(--ps-padding-m);
}

.editor__header {
  display: flex;
  justify-content: space-between;
  gap: var(--ps-padding-s);
}

.editor__headline {
  font-size: var(--ps-font-size-l);
  font-weight: bold;
}

.editor__time {
  font-size: var(--ps-font-size-xs);
  padding: var(--ps-padding-s);
  border: 1px solid var(--ps-panel-border-color);
  border-radius: 5px;
  white-space: nowrap;
  height: min-content;
}

.editor__wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--ps-padding-m);
  flex: 1;
}

.editor__body {
  text-wrap: auto;
  overflow: unset;
}

.editor__secondary-buttons {
  position: sticky;
  top: 0;
}
</style>
